import React, { useEffect, useMemo, useState } from "react";
import Sidebar from "../../components/Sidebar";
import Navbar from "../../components/Navbar";
import toast from "react-hot-toast";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import axiosInstance from "../../../axiosInstance";
import slugify from "slugify";

const MySwal = withReactContent(Swal);

const generateSlug = (name) =>
  slugify(name || "", { lower: true, strict: true, trim: true });

export default function AdminCitiesAreasSinglePage() {
  const [loading, setLoading] = useState(true);

  // dropdowns
  const [countryOptions, setCountryOptions] = useState([]);
  const [stateOptions, setStateOptions] = useState([]);

  // list + filters
  const [cities, setCities] = useState([]);
  const [filterCountryId, setFilterCountryId] = useState("");
  const [filterStateId, setFilterStateId] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [limit, setLimit] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");

  // add city form
  const [addLoading, setAddLoading] = useState(false);
  const [addForm, setAddForm] = useState({
    countryId: "",
    stateId: "",
    name: "",
    slug: "",
    zipcode: "",
    lat: "",
    lng: "",
    status: true,
  });

  // edit modal for city + areas
  const [editOpen, setEditOpen] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [editForm, setEditForm] = useState({
    _id: "",
    countryId: "",
    stateId: "",
    name: "",
    slug: "",
    zipcode: "",
    lat: "",
    lng: "",
    status: true,
    areas: [],
  });

  // area mini-form inside modal
  const [areaForm, setAreaForm] = useState({ name: "", slug: "", pincode: "", status: true });
  const [areaEditId, setAreaEditId] = useState(null);
  const [areaSaving, setAreaSaving] = useState(false);

  const fetchCountriesDropdown = async () => {
    const res = await axiosInstance.get("/admin/countries?page=1&limit=500&search=");
    setCountryOptions(res.data.countries || res.data.Countries || []);
  };

  const fetchStatesByCountry = async (countryId) => {
    if (!countryId) {
      setStateOptions([]);
      return;
    }
    // Admin list endpoint (fast way)
    const res = await axiosInstance.get(`/admin/states?page=1&limit=500&search=&countryId=${countryId}`);
    setStateOptions(res.data.states || res.data.States || []);
  };

  const fetchCities = async () => {
    try {
      setLoading(true);
      const url = `/admin/cities?page=${currentPage}&limit=${limit}&search=${encodeURIComponent(
        searchTerm
      )}&countryId=${filterCountryId || ""}&stateId=${filterStateId || ""}`;

      const res = await axiosInstance.get(url);
      setCities(res.data.cities || res.data.Cities || []);
      setCurrentPage(res.data.currentPage || currentPage);
      setTotalPages(res.data.totalPages || 1);
    } catch (e) {
      toast.error("Error fetching cities");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCountriesDropdown().catch(() => toast.error("Error loading countries"));
  }, []);

  useEffect(() => {
    fetchCities();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, limit]);

  // filter dropdown behavior
  useEffect(() => {
    fetchStatesByCountry(filterCountryId).catch(() => toast.error("Error loading states"));
    setFilterStateId("");
    setCurrentPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterCountryId]);

  const onSearch = () => {
    setCurrentPage(1);
    fetchCities();
  };

  // ADD CITY
  const handleAddChange = (e) => {
    const { name, value } = e.target;

    if (name === "countryId") {
      setAddForm((p) => ({ ...p, countryId: value, stateId: "" }));
      return;
    }

    if (name === "name") {
      setAddForm((p) => ({ ...p, name: value, slug: generateSlug(value) }));
      return;
    }

    setAddForm((p) => ({ ...p, [name]: value }));
  };

  useEffect(() => {
    // load state options for add form when country changes
    fetchStatesByCountry(addForm.countryId).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addForm.countryId]);

  const addCity = async () => {
    if (!addForm.countryId) return toast.error("Select country");
    if (!addForm.stateId) return toast.error("Select state");
    if (!addForm.name.trim()) return toast.error("Enter city name");

    try {
      setAddLoading(true);
      await axiosInstance.post("/admin/city", {
        countryId: addForm.countryId,
        stateId: addForm.stateId,
        name: addForm.name.trim(),
        slug: addForm.slug,
        zipcode: addForm.zipcode.trim(),
        lat: addForm.lat ? Number(addForm.lat) : undefined,
        lng: addForm.lng ? Number(addForm.lng) : undefined,
        status: addForm.status,
      });

      toast.success("City added!");
      setAddForm({ countryId: "", stateId: "", name: "", slug: "", zipcode: "", lat: "", lng: "", status: true });
      setCurrentPage(1);
      fetchCities();
    } catch (e) {
      toast.error(e?.response?.data?.message || "Error adding city");
    } finally {
      setAddLoading(false);
    }
  };

  // EDIT CITY
  const openEdit = (row) => {
    setEditForm({
      _id: row._id,
      countryId: row.countryId?._id || row.countryId || "",
      stateId: row.stateId?._id || row.stateId || "",
      name: row.name || "",
      slug: row.slug || "",
      zipcode: row.zipcode || "",
      lat: row.lat ?? "",
      lng: row.lng ?? "",
      status: row.status === true,
      areas: row.areas || [],
    });

    setAreaForm({ name: "", slug: "", pincode: "", status: true });
    setAreaEditId(null);
    setEditOpen(true);
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;

    if (name === "name") {
      setEditForm((p) => ({ ...p, name: value, slug: generateSlug(value) }));
      return;
    }
    setEditForm((p) => ({ ...p, [name]: value }));
  };

  const updateCity = async () => {
    if (!editForm.countryId) return toast.error("Select country");
    if (!editForm.stateId) return toast.error("Select state");
    if (!editForm.name.trim()) return toast.error("Enter city name");

    try {
      setEditLoading(true);
      await axiosInstance.put(`/admin/city/${editForm._id}`, {
        countryId: editForm.countryId,
        stateId: editForm.stateId,
        name: editForm.name.trim(),
        slug: editForm.slug,
        zipcode: editForm.zipcode.trim(),
        lat: editForm.lat === "" ? undefined : Number(editForm.lat),
        lng: editForm.lng === "" ? undefined : Number(editForm.lng),
        status: editForm.status,
      });

      toast.success("City updated!");
      setEditOpen(false);
      fetchCities();
    } catch (e) {
      toast.error(e?.response?.data?.message || "Error updating city");
    } finally {
      setEditLoading(false);
    }
  };

  const deleteCity = (id) => {
    MySwal.fire({
      title: "Are you sure?",
      text: "You will not be able to recover this city!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "No, cancel!",
      reverseButtons: true,
    }).then(async (result) => {
      if (!result.isConfirmed) return;
      try {
        await axiosInstance.delete(`/admin/city/${id}`);
        toast.success("City deleted!");
        fetchCities();
      } catch (e) {
        toast.error(e?.response?.data?.message || "Error deleting city");
      }
    });
  };

  // AREA CRUD inside modal
  const handleAreaChange = (e) => {
    const { name, value } = e.target;
    if (name === "name") {
      setAreaForm((p) => ({ ...p, name: value, slug: generateSlug(value) }));
      return;
    }
    setAreaForm((p) => ({ ...p, [name]: value }));
  };

  const startEditArea = (area) => {
    setAreaEditId(area._id);
    setAreaForm({
      name: area.name || "",
      slug: area.slug || generateSlug(area.name || ""),
      pincode: area.pincode || "",
      status: area.status === true,
    });
  };

  const resetAreaForm = () => {
    setAreaEditId(null);
    setAreaForm({ name: "", slug: "", pincode: "", status: true });
  };

  const saveArea = async () => {
    if (!areaForm.name.trim()) return toast.error("Enter area name");

    try {
      setAreaSaving(true);
      if (areaEditId) {
        // update
        await axiosInstance.put(`/admin/city/${editForm._id}/area/${areaEditId}`, {
          name: areaForm.name.trim(),
          slug: areaForm.slug,
          pincode: areaForm.pincode.trim(),
          status: areaForm.status,
        });
        toast.success("Area updated!");
      } else {
        // add
        await axiosInstance.post(`/admin/city/${editForm._id}/area`, {
          name: areaForm.name.trim(),
          slug: areaForm.slug,
          pincode: areaForm.pincode.trim(),
          status: areaForm.status,
        });
        toast.success("Area added!");
      }

      // refresh single city from server (best)
      const res = await axiosInstance.get(`/admin/city/${editForm._id}`);
      const city = res.data.city || res.data.City || res.data.data || res.data;
      setEditForm((p) => ({ ...p, areas: city.areas || [] }));
      resetAreaForm();
    } catch (e) {
      toast.error(e?.response?.data?.message || "Error saving area");
    } finally {
      setAreaSaving(false);
    }
  };

  const deleteArea = (areaId) => {
    MySwal.fire({
      title: "Delete area?",
      text: "This will remove the area from city.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete!",
      cancelButtonText: "Cancel",
      reverseButtons: true,
    }).then(async (result) => {
      if (!result.isConfirmed) return;

      try {
        await axiosInstance.delete(`/admin/city/${editForm._id}/area/${areaId}`);
        toast.success("Area deleted!");

        const res = await axiosInstance.get(`/admin/city/${editForm._id}`);
        const city = res.data.city || res.data.City || res.data.data || res.data;
        setEditForm((p) => ({ ...p, areas: city.areas || [] }));

        if (areaEditId === areaId) resetAreaForm();
      } catch (e) {
        toast.error(e?.response?.data?.message || "Error deleting area");
      }
    });
  };

  return (
    <>
      <Sidebar />
      <main className="content">
        <Navbar />

        <div className="py-4">
          <h1 className="h4">Cities + Areas  </h1>
        </div>

        {/* ADD CITY FORM */}
        <div className="card mb-4">
          <div className="card-body">
            <div className="row g-3 align-items-end">
              <div className="col-md-3">
                <label className="form-label">Country</label>
                <select className="form-control" name="countryId" value={addForm.countryId} onChange={handleAddChange}>
                  <option value="">Select</option>
                  {countryOptions.map((c) => (
                    <option key={c._id} value={c._id}>{c.name}</option>
                  ))}
                </select>
              </div>

              <div className="col-md-3">
                <label className="form-label">State</label>
                <select className="form-control" name="stateId" value={addForm.stateId} onChange={handleAddChange}>
                  <option value="">Select</option>
                  {stateOptions.map((s) => (
                    <option key={s._id} value={s._id}>{s.name}</option>
                  ))}
                </select>
              </div>

              <div className="col-md-3">
                <label className="form-label">City Name</label>
                <input className="form-control" name="name" value={addForm.name} onChange={handleAddChange} />
              </div>

              <div className="col-md-3">
                <label className="form-label">Slug</label>
                <input className="form-control" value={addForm.slug} readOnly />
              </div>

              <div className="col-md-2">
                <label className="form-label">Zipcode</label>
                <input className="form-control" name="zipcode" value={addForm.zipcode} onChange={handleAddChange} />
              </div>

              <div className="col-md-2">
                <label className="form-label">Lat</label>
                <input className="form-control" name="lat" value={addForm.lat} onChange={handleAddChange} />
              </div>

              <div className="col-md-2">
                <label className="form-label">Lng</label>
                <input className="form-control" name="lng" value={addForm.lng} onChange={handleAddChange} />
              </div>

              <div className="col-md-3">
                <label className="form-label">Status</label>
                <div className="d-flex gap-3">
                  <div className="form-check">
                    <input className="form-check-input" type="radio" checked={addForm.status === true} onChange={() => setAddForm((p) => ({ ...p, status: true }))} />
                    <label className="form-check-label text-success">Active</label>
                  </div>
                  <div className="form-check">
                    <input className="form-check-input" type="radio" checked={addForm.status === false} onChange={() => setAddForm((p) => ({ ...p, status: false }))} />
                    <label className="form-check-label text-danger">Inactive</label>
                  </div>
                </div>
              </div>

              <div className="col-12">
                <button className="btn btn-primary" type="button" onClick={addCity} disabled={addLoading}>
                  {addLoading ? "Adding..." : "Add City"}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* LIST + FILTERS */}
        <div className="card">
          <div className="table-responsive py-4">
            <div className="datatable-top px-3">
              <div className="datatable-dropdown">
                <label>
                  <select className="datatable-selector" value={limit} onChange={(e) => { setLimit(parseInt(e.target.value, 10)); setCurrentPage(1); }}>
                    <option value={5}>5</option>
                    <option value={10}>10</option>
                    <option value={15}>15</option>
                    <option value={20}>20</option>
                    <option value={25}>25</option>
                  </select>{" "}
                  entries per page
                </label>
              </div>

              <div className="d-flex align-items-center gap-2">
                <select className="form-control" style={{ width: 220 }} value={filterCountryId} onChange={(e) => setFilterCountryId(e.target.value)}>
                  <option value="">All Countries</option>
                  {countryOptions.map((c) => (
                    <option key={c._id} value={c._id}>{c.name}</option>
                  ))}
                </select>

                <select className="form-control" style={{ width: 220 }} value={filterStateId} onChange={(e) => { setFilterStateId(e.target.value); setCurrentPage(1); }}>
                  <option value="">All States</option>
                  {stateOptions.map((s) => (
                    <option key={s._id} value={s._id}>{s.name}</option>
                  ))}
                </select>

                <div className="datatable-search">
                  <input className="datatable-input" type="text" placeholder="Search city..." value={searchTerm} onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }} />
                </div>

                <button className="btn btn-primary" onClick={onSearch}>Search</button>
              </div>
            </div>

            {loading ? (
              <div className="p-4">Loading...</div>
            ) : (
              <>
                <table className="table table-flush">
                  <thead>
                    <tr>
                      <th>City</th>
                      <th>Slug</th>
                      <th>State</th>
                      <th>Country</th>
                      <th>Areas</th>
                      <th>Status</th>
                      <th style={{ width: 220 }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cities.map((row) => (
                      <tr key={row._id}>
                        <td>{row.name}</td>
                        <td>{row.slug || "-"}</td>
                        <td>{row.stateId?.name || "-"}</td>
                        <td>{row.countryId?.name || "-"}</td>
                        <td>{(row.areas || []).length}</td>
                        <td>
                          <span className={`badge ${row.status ? "bg-success" : "bg-danger"}`}>
                            {row.status ? "Active" : "Inactive"}
                          </span>
                        </td>
                        <td>
                          <button className="btn btn-primary me-2" onClick={() => openEdit(row)}>Edit</button>
                          <button className="btn btn-danger" onClick={() => deleteCity(row._id)}>Delete</button>
                        </td>
                      </tr>
                    ))}
                    {!cities.length && (
                      <tr><td colSpan={7} className="text-center py-4">No cities found</td></tr>
                    )}
                  </tbody>
                </table>

                <div className="datatable-bottom px-3">
                  <nav className="datatable-pagination">
                    <ul className="datatable-pagination-list">
                      <li className={`datatable-pagination-list-item ${currentPage === 1 ? "datatable-hidden" : "datatable-active"}`}>
                        <button className="datatable-pagination-list-item-link" onClick={() => setCurrentPage((p) => Math.max(1, p - 1))} disabled={currentPage === 1}>‹</button>
                      </li>
                      <li>
                        <div className="datatable-info py-1 px-3">
                          Page <strong>{currentPage}</strong> of <strong>{totalPages}</strong>
                        </div>
                      </li>
                      <li className={`datatable-pagination-list-item ${currentPage === totalPages ? "datatable-hidden" : "datatable-active"}`}>
                        <button className="datatable-pagination-list-item-link" onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}>›</button>
                      </li>
                    </ul>
                  </nav>
                </div>
              </>
            )}
          </div>
        </div>

        {/* EDIT MODAL (CITY + AREA CRUD) */}
        {editOpen && (
          <div className="modal fade show d-block" tabIndex="-1" role="dialog" style={{ background: "rgba(0,0,0,.45)" }}>
            <div className="modal-dialog modal-xl" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Edit City + Areas</h5>
                  <button type="button" className="btn-close" onClick={() => setEditOpen(false)} />
                </div>

                <div className="modal-body">
                  {/* City form */}
                  <div className="row g-3">
                    <div className="col-md-4">
                      <label className="form-label">City Name</label>
                      <input className="form-control" name="name" value={editForm.name} onChange={handleEditChange} />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Slug</label>
                      <input className="form-control" value={editForm.slug} readOnly />
                    </div>
                    <div className="col-md-2">
                      <label className="form-label">Zipcode</label>
                      <input className="form-control" name="zipcode" value={editForm.zipcode} onChange={handleEditChange} />
                    </div>
                    <div className="col-md-1">
                      <label className="form-label">Lat</label>
                      <input className="form-control" name="lat" value={editForm.lat} onChange={handleEditChange} />
                    </div>
                    <div className="col-md-1">
                      <label className="form-label">Lng</label>
                      <input className="form-control" name="lng" value={editForm.lng} onChange={handleEditChange} />
                    </div>
                    <div className="col-md-12">
                      <label className="form-label">Status</label>
                      <div className="d-flex gap-3">
                        <div className="form-check">
                          <input className="form-check-input" type="radio" checked={editForm.status === true} onChange={() => setEditForm((p) => ({ ...p, status: true }))} />
                          <label className="form-check-label text-success">Active</label>
                        </div>
                        <div className="form-check">
                          <input className="form-check-input" type="radio" checked={editForm.status === false} onChange={() => setEditForm((p) => ({ ...p, status: false }))} />
                          <label className="form-check-label text-danger">Inactive</label>
                        </div>
                      </div>
                    </div>
                  </div>

                  <hr className="my-4" />

                  {/* Area form */}
                  <h5 className="mb-3">Areas</h5>
                  <div className="row g-3 align-items-end">
                    <div className="col-md-4">
                      <label className="form-label">Area Name</label>
                      <input className="form-control" name="name" value={areaForm.name} onChange={handleAreaChange} />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">Slug</label>
                      <input className="form-control" value={areaForm.slug} readOnly />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">Pincode</label>
                      <input className="form-control" name="pincode" value={areaForm.pincode} onChange={handleAreaChange} />
                    </div>
                    <div className="col-md-2">
                      <label className="form-label">Status</label>
                      <div className="d-flex gap-3">
                        <div className="form-check">
                          <input className="form-check-input" type="radio" checked={areaForm.status === true} onChange={() => setAreaForm((p) => ({ ...p, status: true }))} />
                          <label className="form-check-label text-success">Active</label>
                        </div>
                        <div className="form-check">
                          <input className="form-check-input" type="radio" checked={areaForm.status === false} onChange={() => setAreaForm((p) => ({ ...p, status: false }))} />
                          <label className="form-check-label text-danger">Inactive</label>
                        </div>
                      </div>
                    </div>

                    <div className="col-12 d-flex gap-2">
                      <button className="btn btn-primary" type="button" onClick={saveArea} disabled={areaSaving}>
                        {areaSaving ? "Saving..." : areaEditId ? "Update Area" : "Add Area"}
                      </button>
                      <button className="btn btn-secondary" type="button" onClick={resetAreaForm} disabled={areaSaving}>
                        Clear
                      </button>
                    </div>
                  </div>

                  {/* Areas table */}
                  <div className="table-responsive mt-4">
                    <table className="table table-flush">
                      <thead>
                        <tr>
                          <th>Area</th>
                          <th>Slug</th>
                          <th>Pincode</th>
                          <th>Status</th>
                          <th style={{ width: 200 }}>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(editForm.areas || []).map((a) => (
                          <tr key={a._id}>
                            <td>{a.name}</td>
                            <td>{a.slug || "-"}</td>
                            <td>{a.pincode || "-"}</td>
                            <td>
                              <span className={`badge ${a.status ? "bg-success" : "bg-danger"}`}>
                                {a.status ? "Active" : "Inactive"}
                              </span>
                            </td>
                            <td>
                              <button className="btn btn-primary me-2" onClick={() => startEditArea(a)}>Edit</button>
                              <button className="btn btn-danger" onClick={() => deleteArea(a._id)}>Delete</button>
                            </td>
                          </tr>
                        ))}
                        {!editForm.areas?.length && (
                          <tr><td colSpan={5} className="text-center py-3">No areas</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="modal-footer">
                  <button className="btn btn-secondary" onClick={() => setEditOpen(false)} disabled={editLoading}>Close</button>
                  <button className="btn btn-primary" onClick={updateCity} disabled={editLoading}>
                    {editLoading ? "Updating..." : "Update City"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
    </>
  );
}